import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  ENTERPRISE_INTAKE_BADGE_AGENCY,
  ENTERPRISE_INTAKE_BADGE_BRAND,
  ENTERPRISE_INTAKE_CONFIDENTIALITY_NOTE,
  ENTERPRISE_INTAKE_SECTION_01_CONTACT,
  ENTERPRISE_INTAKE_SECTION_02_MODULE_SELECTOR,
  ENTERPRISE_INTAKE_SECTION_MODULE_1_CREATIVE_ASSETS,
  ENTERPRISE_INTAKE_SECTION_MODULE_2_PAID_MEDIA_LAUNCH,
  ENTERPRISE_INTAKE_SECTION_PLAN,
  ENTERPRISE_INTAKE_SUBMIT_BUTTON,
  ENTERPRISE_INTAKE_SUBMIT_SUCCESS_MESSAGE,
  ENTERPRISE_INTAKE_SUBMIT_SUCCESS_TITLE,
  ERROR_OCCURRED,
  OK,
} from "../../i18n/keysTranslations";
import { SimpleAlert } from "../../components/Alerts/Alerts";
import { saveEnterpriseIntakeFormRequest } from "../../actions/enterpriseIntake";
import Navbar from "../PreActivation/components/Navbar";
import Card from "../PreActivation/components/Card";
import { PAPER_BACKGROUND_COLOR_SLIDE_MENU } from "../../utils/colors";
import {
  getBrowserAppLanguage,
  normalizeAppLanguage,
  persistAppLanguage,
} from "../../utils/i18nLanguage";
import { createEnterpriseIntakeSchema } from "./enterpriseIntakeSchema";
import Button from "../../components/Buttons/Button";
import useFocusErrorForm from "../../hooks/useFocusErrorForm";
import LanguageSwitcher from "../../components/Common/LanguageSwitcher";
import i18next from "i18next";
import HeroSection from "./sections/HeroSection";
import ContactSection from "./sections/ContactSection";
import ScopeSection from "./sections/ScopeSection";
import CreativeAssetsSection from "./sections/modules/Module1CreativeAssetsSection";
import CampaignsSection from "./sections/CampaignsSection";
import PlanSection from "./sections/PlanSection";
import {
  applyEnterpriseIntakeConditionalCleanup,
  buildFormValuesForOrganizationType,
  isEnterpriseIntakeAgency,
  sanitizeEnterpriseIntakeSubmitValues,
} from "./enterpriseIntakeFormValues";
import { getEnterpriseIntakeDefaultValues } from "./enterpriseIntakeDefaultValues";
import { getEnterpriseIntakeSectionVisibility } from "./useEnterpriseIntakeModuleVisibility";

import { LaunchError } from "../../utils/errors";
import { store } from "../../app/store";

// eslint-disable-next-line react-refresh/only-export-components
export const getBrowserLanguage = getBrowserAppLanguage;

const LANGUAGE_SWITCHER_BUTTON_STYLES = {
  minWidth: "max-content",
  border: "none !important",
  boxShadow: "none !important",
  backgroundColor: "transparent !important",
  px: "0 !important",
  "&.MuiButtonBase-root.MuiButton-root:hover": {
    backgroundColor: "transparent !important",
    border: "none !important",
    boxShadow: "none !important",
    color: "inherit !important",
  },
};

const LANGUAGE_SWITCHER_MENU_STYLES = {
  "& .MuiPaper-root": {
    marginTop: "8px",
  },
};

const EnterpriseIntakeSectionCards = ({ control, errors, readOnly }) => {
  const { t } = useTranslation();
  const selectedAdleModules =
    useWatch({ control, name: "selectedAdleModules" }) ?? [];
  const organizationType = useWatch({ control, name: "organizationType" });
  const mediaManagementAddon = useWatch({
    control,
    name: "mediaManagementAddon",
  });
  const formValues = useWatch({ control });
  const {
    showCreativeAssets,
    showCampaigns,
    showOptimizationFrequency,
    showWhiteLabel,
    showMediaManagementAddon,
    showMediaSpend,
  } = getEnterpriseIntakeSectionVisibility({
    selectedAdleModules,
    organizationType,
    mediaManagementAddon,
    formValues,
    readOnly,
  });

  return (
    <>
      <Card section={t(ENTERPRISE_INTAKE_SECTION_01_CONTACT)}>
        <ContactSection control={control} errors={errors} readOnly={readOnly} />
      </Card>

      <Card section={t(ENTERPRISE_INTAKE_SECTION_02_MODULE_SELECTOR)}>
        <ScopeSection control={control} errors={errors} readOnly={readOnly} />
      </Card>

      {showCreativeAssets ? (
        <Card section={t(ENTERPRISE_INTAKE_SECTION_MODULE_1_CREATIVE_ASSETS)}>
          <CreativeAssetsSection
            control={control}
            errors={errors}
            readOnly={readOnly}
          />
        </Card>
      ) : null}

      {showCampaigns ? (
        <Card section={t(ENTERPRISE_INTAKE_SECTION_MODULE_2_PAID_MEDIA_LAUNCH)}>
          <CampaignsSection
            control={control}
            errors={errors}
            readOnly={readOnly}
            showOptimizationFrequency={showOptimizationFrequency}
          />
        </Card>
      ) : null}

      <Card section={t(ENTERPRISE_INTAKE_SECTION_PLAN)}>
        <PlanSection
          control={control}
          errors={errors}
          readOnly={readOnly}
          showWhiteLabel={showWhiteLabel}
          showMediaManagementAddon={showMediaManagementAddon}
          showMediaSpend={showMediaSpend}
        />
      </Card>
    </>
  );
};

const EnterpriseIntakePageShell = ({ children, readOnly }) => (
  <Box
    sx={{
      minHeight: "100vh",
      background: PAPER_BACKGROUND_COLOR_SLIDE_MENU,
      padding: "24px 16px 48px",
    }}
  >
    <Container
      maxWidth={false}
      disableGutters
      sx={{
        width: "100%",
        maxWidth: readOnly ? "100%" : "640px",
        margin: "0 auto",
      }}
    >
      {children}
    </Container>
  </Box>
);

const OwnerBackBar = ({ backTo }) => {
  const navigate = useNavigate();
  return (
    <Box sx={{ display: "flex", alignItems: "center", marginBottom: "16px" }}>
      <IconButton
        aria-label="back"
        onClick={() => navigate(backTo)}
        edge="start"
        size="small"
        sx={{ color: "text.primary" }}
      >
        <ArrowBackIcon />
      </IconButton>
    </Box>
  );
};

const EnterpriseIntakeSubmitForm = ({
  onSubmit,
  submitError,
  isSaving,
  children,
}) => {
  const { t } = useTranslation();

  return (
    <Box component="form" onSubmit={onSubmit} sx={{ width: "100%" }} noValidate>
      {children}

      {submitError ? (
        <Typography
          role="alert"
          sx={{
            color: "error.main",
            fontSize: "14px",
            textAlign: "center",
            mb: 1,
          }}
        >
          {submitError}
        </Typography>
      ) : null}

      <Button
        type="submit"
        fullWidth
        color="primary"
        disabled={isSaving}
        sx={{
          mt: 1,
          py: 1.5,
          fontWeight: 600,
          borderRadius: "9px !important",
        }}
      >
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
          }}
        >
          {isSaving ? (
            <CircularProgress size={22} sx={{ color: "inherit" }} />
          ) : null}
          <Box component="span">{t(ENTERPRISE_INTAKE_SUBMIT_BUTTON)}</Box>
        </Box>
      </Button>

      <Typography
        component="p"
        variant="body2"
        sx={{
          mt: 2,
          color: "text.secondary",
          textAlign: "center",
        }}
      >
        {t(ENTERPRISE_INTAKE_CONFIDENTIALITY_NOTE)}
      </Typography>
    </Box>
  );
};

const useEnterpriseIntakeLanguage = ({ readOnly = false }) => {
  const navigate = useNavigate();
  const { lang } = useParams();
  const routeLanguage = normalizeAppLanguage(lang);
  const currentLanguage = readOnly
    ? normalizeAppLanguage(i18next.language) || "en"
    : routeLanguage;

  useEffect(() => {
    if (readOnly) return;
    if (!currentLanguage) {
      navigate(`/enterprise-intake/${getBrowserLanguage()}`, { replace: true });
      return;
    }
    if (i18next.language !== currentLanguage) {
      i18next.changeLanguage(currentLanguage);
    }
  }, [currentLanguage, navigate, readOnly]);

  const onChangeLanguage = (language) => {
    const nextLanguage = normalizeAppLanguage(language);
    if (!nextLanguage || nextLanguage === currentLanguage) return;
    i18next.changeLanguage(nextLanguage);
    persistAppLanguage(nextLanguage);
    if (!readOnly) {
      navigate(`/enterprise-intake/${nextLanguage}`);
    }
  };

  return {
    currentLanguage,
    onChangeLanguage,
    shouldWaitForValidLanguage: !readOnly && !currentLanguage,
  };
};

const CONDITIONAL_CLEANUP_FIELDS = [
  "module1MonthlyNewImagesCount",
  "module1MonthlyImageAnimationsCount",
  "module1MonthlyVideosCount",
  "creativesBufferTypes",
  "creativesBufferPercent",
  "creativesBufferPercentOther",
  "module2MonthlyCampaignsCount",
  "optimizationFrequency",
  "whiteLabelRequirement",
  "mediaManagementAddon",
  "monthlyMediaSpendUsd",
];

const EnterpriseIntake = ({
  readOnly = false,
  prefilledData = null,
  ownerBackTo = null,
}) => {
  const { t } = useTranslation();
  const [submitError, setSubmitError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const { currentLanguage, onChangeLanguage, shouldWaitForValidLanguage } =
    useEnterpriseIntakeLanguage({ readOnly });

  const validationSchema = useMemo(() => createEnterpriseIntakeSchema(t), [t]);

  const { control, handleSubmit, reset, getValues, setValue, formState } =
    useForm({
      resolver: readOnly ? undefined : zodResolver(validationSchema),
      criteriaMode: "all",
      defaultValues: getEnterpriseIntakeDefaultValues(),
      mode: readOnly ? "onChange" : "onSubmit",
      reValidateMode: "onChange",
      shouldFocusError: !readOnly,
    });

  const { errors } = formState;
  const selectedAdleModules = useWatch({
    control,
    name: "selectedAdleModules",
  });
  const creativesBufferTypes = useWatch({
    control,
    name: "creativesBufferTypes",
  });
  const mediaManagementAddon = useWatch({
    control,
    name: "mediaManagementAddon",
  });
  const organizationType = useWatch({ control, name: "organizationType" });

  useFocusErrorForm(formState);

  useEffect(() => {
    if (prefilledData) {
      reset(prefilledData);
    }
  }, [prefilledData, reset]);

  useEffect(() => {
    if (readOnly) return;
    const currentValues = getValues();
    const nextValues = applyEnterpriseIntakeConditionalCleanup(currentValues);
    CONDITIONAL_CLEANUP_FIELDS.forEach((field) => {
      const currentValue = currentValues[field];
      const nextValue = nextValues[field];
      const hasChanged = Array.isArray(currentValue)
        ? JSON.stringify(currentValue) !== JSON.stringify(nextValue)
        : currentValue !== nextValue;
      if (hasChanged) {
        setValue(field, nextValue, {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: false,
        });
      }
    });
  }, [
    creativesBufferTypes,
    getValues,
    mediaManagementAddon,
    organizationType,
    readOnly,
    selectedAdleModules,
    setValue,
  ]);

  if (shouldWaitForValidLanguage) return null;

  const onOrganizationTypeChange = ({ organizationType: nextType }) => {
    setSubmitError(null);
    reset(
      buildFormValuesForOrganizationType({
        organizationType: nextType,
        currentValues: getValues(),
      })
    );
  };

  const onSubmitValid = async (formValues) => {
    setSubmitError(null);
    setIsSaving(true);
    let data;
    try {
      data = await saveEnterpriseIntakeFormRequest(
        sanitizeEnterpriseIntakeSubmitValues(formValues)
      );
    } catch (err) {
      store.dispatch(
        LaunchError(err, false, [], {
          functionName: "onSubmitValid",
          capture: "exception",
        })
      );
      const message =
        err?.response?.data?.message ?? err?.message ?? t(ERROR_OCCURRED);
      setSubmitError(message);
      setIsSaving(false);
      return;
    }
    setIsSaving(false);

    if (data?.ok) {
      reset(getEnterpriseIntakeDefaultValues(), {
        keepDefaultValues: false,
        keepDirty: false,
        keepErrors: false,
        keepIsSubmitted: false,
        keepIsValid: false,
        keepSubmitCount: false,
        keepTouched: false,
      });
      await SimpleAlert({
        title: t(ENTERPRISE_INTAKE_SUBMIT_SUCCESS_TITLE),
        text: t(ENTERPRISE_INTAKE_SUBMIT_SUCCESS_MESSAGE),
        icon: "success",
        confirmButtonText: t(OK),
      });
      return;
    }
    setSubmitError(data?.message ?? t(ERROR_OCCURRED));
  };

  const onSubmitInvalid = () => {
    setSubmitError(null);
  };

  const sectionCards = (
    <EnterpriseIntakeSectionCards
      control={control}
      errors={errors}
      readOnly={readOnly}
    />
  );

  return (
    <Box sx={{ position: "relative" }}>
      {!readOnly ? (
        <Box
          sx={{
            position: "absolute",
            top: 40,
            right: 16,
            zIndex: 2,
          }}
        >
          <LanguageSwitcher
            language={currentLanguage}
            onChangeLanguage={onChangeLanguage}
            colorButton="secondary"
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            sx={LANGUAGE_SWITCHER_BUTTON_STYLES}
            sxMenu={LANGUAGE_SWITCHER_MENU_STYLES}
          />
        </Box>
      ) : null}

      <EnterpriseIntakePageShell readOnly={readOnly}>
        {ownerBackTo ? <OwnerBackBar backTo={ownerBackTo} /> : null}
        {!readOnly ? <Navbar /> : null}
        {!readOnly ? (
          <Card
            badgeKey={
              isEnterpriseIntakeAgency(organizationType)
                ? ENTERPRISE_INTAKE_BADGE_AGENCY
                : ENTERPRISE_INTAKE_BADGE_BRAND
            }
          >
            <HeroSection
              control={control}
              readOnly={readOnly}
              onOrganizationTypeChange={onOrganizationTypeChange}
            />
          </Card>
        ) : null}

        {readOnly ? (
          <Box sx={{ width: "100%" }}>{sectionCards}</Box>
        ) : (
          <EnterpriseIntakeSubmitForm
            onSubmit={handleSubmit(onSubmitValid, onSubmitInvalid)}
            submitError={submitError}
            isSaving={isSaving}
          >
            {sectionCards}
          </EnterpriseIntakeSubmitForm>
        )}
      </EnterpriseIntakePageShell>
    </Box>
  );
};

export default EnterpriseIntake;
